import type { EmployeeCandidate } from './ports/registrationReviewPort';
import type { PendingRegistrationRow } from './ports/progressPort';

/**
 * What a reviewer is allowed to do with a waiting registration, and why.
 *
 * Pure, so the rules a committee member is held to can be read and tested
 * without rendering anything (§5.4). The server checks all of this again — it
 * is the boundary — but a reviewer should be told *before* pressing a button
 * that a record is already spoken for.
 */

export const REVIEW_REASON_LABELS_AR: Readonly<
  Record<PendingRegistrationRow['reason'], string>
> = {
  name_mismatch: 'رفض الاسم المعروض على بريده',
  no_matching_record: 'لا يوجد سجل يحمل بريده',
};

/** Why a decision cannot be made yet. Null when approving is permitted. */
export type ApprovalBlock = 'no_candidate' | 'record_taken';

export const APPROVAL_BLOCK_MESSAGES_AR: Readonly<Record<ApprovalBlock, string>> = {
  no_candidate: 'ابحث عن السجل بالرقم الوظيفي أولًا.',
  record_taken: 'هذا السجل مرتبط بحساب آخر. سجل واحد لحساب واحد.',
};

export function approvalBlock(candidate: EmployeeCandidate | null): ApprovalBlock | null {
  if (candidate === null) return 'no_candidate';
  if (candidate.alreadyLinked) return 'record_taken';
  return null;
}

/**
 * Whether the reviewer should look twice before linking.
 *
 * The applicant typed their own name; the roster holds another. Comparing them
 * by hand is the entire purpose of this queue, so the screen says plainly when
 * the two do not match rather than letting a reviewer approve on autopilot.
 */
export function namesAgree(applicantName: string, recordName: string): boolean {
  const normalise = (value: string) =>
    value
      .trim()
      .replace(/\s+/g, ' ')
      // Alef, ya and ta-marbuta are written inconsistently across the roster
      // and by people typing their own names; a difference in those alone is
      // not a different person.
      .replace(/[أإآ]/g, 'ا')
      .replace(/ى/g, 'ي')
      .replace(/ة/g, 'ه');

  return normalise(applicantName) === normalise(recordName);
}

/** A rejection must say why: the applicant is shown this sentence. */
export function isUsableReason(reasonAr: string): boolean {
  return reasonAr.trim().length >= 4;
}
